/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import { ENV_LOCALE } from '@hems/util/src/constant/constant';
import { LANGUAGE } from '@hems/util/src/constant/languages';
import { TERMS_CATEGORY } from '@hems/util/src/constant/terms';
import type { TermsCategoryValue } from '@hems/util/src/constant/terms';
import {
  getLocalDate,
  getLongFormTimeZone,
  getShortFormTimeZone,
  getTimezoneDate,
  getTimeZoneOffset,
} from '@hems/util/src/helper/dateHelper';

import type { EnvLocale, LangCd } from 'hems';

import type { EffectiveTermsInfo } from 'hems/terms';

const categoryNameFilter = (termsList: EffectiveTermsInfo[], categoryName: TermsCategoryValue) => {
  return termsList.filter((item) => item.terms_nm === categoryName);
};

const localeFilter = (termsList: EffectiveTermsInfo[], locale: EnvLocale) => {
  return termsList.filter((item) => item.cntry_cd === locale);
};

const languageFilter = (termsList: EffectiveTermsInfo[], lang: LangCd) => {
  return termsList.filter((item) => item.lang === lang);
};

export const getPrivacyPolicyTerms = (terms: EffectiveTermsInfo[]): EffectiveTermsInfo => {
  const localeFilteredTerms = localeFilter(terms, ENV_LOCALE.US);
  const [privacyTerms] = languageFilter(
    categoryNameFilter(localeFilteredTerms, TERMS_CATEGORY.TERMS_OF_PRIVACY),
    LANGUAGE.EN
  );

  const defaultContents = {
    terms_contents: '',
  };

  return privacyTerms ?? defaultContents;
};

export const getFullDateString = (dateString: string): string =>
  `${dateString}T00:00:00${getTimeZoneOffset(true, dateString)}`;

export const getEffectiveDateStartDate = (
  dateString: string,
  timezone: string,
  location: string,
  isDefaultStartTime: boolean
): string => {
  const fullDateString = getFullDateString(dateString);
  const milliseconds = new Date(fullDateString).getTime();

  const dateStringByTimezone = getLocalDate(milliseconds, { isTime: true, needTimezoneConvert: true, timezone });
  const shortFormTimezone = getShortFormTimeZone(timezone);
  const longFormTimezone = getLongFormTimeZone(timezone);

  const dateStringWithOffset = getTimezoneDate(milliseconds, { isTime: true, timezone });

  return isDefaultStartTime
    ? `${dateStringByTimezone} ${shortFormTimezone} (${longFormTimezone}, ${location})`
    : dateStringWithOffset;
};
